import {IncidentType} from "@/lib/types/statuspages.ts";
import {isBefore, parseISO, startOfDay, subDays} from "date-fns";
import {fromZonedTime} from "date-fns-tz";
import type {PublicStatusPage, PublicStatusPageIncident} from "../../types.ts";
import {GlobalStatusType} from "./InspireGlobalStatus.tsx";

export const computeGlobalStatus = (
  statusPage: PublicStatusPage,
  allIncidents: PublicStatusPageIncident[]
): {status: GlobalStatusType | undefined; durationSecs: number | null} => {
  const now = new Date();
  const activeIncidents = statusPage.active_incidents.filter((i) => i.incident_type === IncidentType.INCIDENT);
  const activeMaintenance = statusPage.active_incidents.filter(
    (i) => i.incident_type === IncidentType.SCHEDULED_MAINTENANCE && isBefore(parseISO(i.starts_at), now)
  );

  if (activeIncidents.length > 0) {
    return {
      status: GlobalStatusType.ISSUES,
      durationSecs: activeIncidents.at(-1)?.duration ?? null,
    };
  }

  if (activeMaintenance.length > 0) {
    const endsAt = activeMaintenance[0].ends_at;
    const durationSecs = endsAt ? (parseISO(endsAt).getTime() - now.getTime()) / 1000 : null;
    return {status: GlobalStatusType.MAINTENANCE, durationSecs};
  }

  if (!statusPage.global_is_operational) {
    // Severity wins over maintenance: a page with both a failing component and a
    // component under maintenance reports issues, not maintenance.
    if (statusPage.has_components_under_critical_state) {
      return {status: GlobalStatusType.ISSUES, durationSecs: null};
    }
    if (statusPage.has_components_under_maintenance_state) {
      return {status: GlobalStatusType.MAINTENANCE, durationSecs: null};
    }
    // Non-operational for a reason the banner has no wording for (a `notification`
    // component) — render nothing rather than claim an outage.
    return {status: undefined, durationSecs: null};
  }

  // All operational — compute how long since the last incident ended
  const activeIds = new Set(statusPage.active_incidents.map((i) => i.id));
  const lastPastIncident = allIncidents
    .filter((i) => !activeIds.has(i.id) && i.incident_type === IncidentType.INCIDENT && i.ends_at !== null)
    .sort((a, b) => parseISO(b.ends_at!).getTime() - parseISO(a.ends_at!).getTime())[0];

  let durationSecs: number | null;
  if (lastPastIncident?.ends_at) {
    durationSecs = (now.getTime() - parseISO(lastPastIncident.ends_at).getTime()) / 1000;
  } else {
    // Fallback: no past incident in range — use start of history window.
    // Legacy: browser gets YYYYMMDD of (now - (range-1) days) in local TZ,
    // backend treats that date as midnight in the SP timezone.
    const historyDays = Math.max((statusPage.default_history_date_range || 7) - 1, 0);
    const localStart = startOfDay(subDays(now, historyDays));
    // Get the UTC instant for midnight of that date in the SP timezone
    const historyStartUtc = fromZonedTime(localStart, statusPage.used_timezone);
    durationSecs = (now.getTime() - historyStartUtc.getTime()) / 1000;
  }

  return {status: GlobalStatusType.OPERATIONAL, durationSecs};
};
